import { Dialog, Portal, useDialog } from "@ark-ui/react";
import {
  DELETION_FLAG,
  ERROR_CODE,
  FALL_RISK,
  FIM_DOMAIN,
  FIM_ITEM,
  INDEPENDENCE_LEVEL,
  INDEPENDENCE_LEVEL_TYPE,
  RESIDENT_STATE,
  TIME_FRAME,
  TimeSchema,
  type UserKey,
  calculateResidentStateDurations,
  calculateSleepingHours,
  parse,
  sort,
  t as tt,
} from "@packages/core";
import { useForm, useSelector } from "@tanstack/react-form";
import { Link, createFileRoute } from "@tanstack/react-router";
import React, { Fragment, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LuAlarmClock,
  LuArrowDownRight,
  LuArrowRight,
  LuArrowUpRight,
  LuCamera,
  LuChevronLeft,
  LuChevronRight,
  LuClock,
  LuLoaderCircle,
  LuMegaphone,
  LuMoonStar,
  LuOctagonAlert,
  LuTrash2,
} from "react-icons/lu";
import { useAsyncFn } from "react-use";
import { toast } from "sonner";
import { Temporal } from "temporal-polyfill";
import { type Merge } from "type-fest";
import * as v from "valibot";
import {
  AvatarPlaceholder,
  Breadcrumbs,
  ButtonStyle,
  ButtonStyle2,
  DialogStyle,
  FallRiskDisplay,
  FimDomainIcon,
  FormField,
  FormFieldInput,
  FormFieldInputStyle,
  PageName,
  PaperStyle,
  Spinner,
  StatusBarThumbnail,
  SwitchStyle,
  TimelineThumbnail,
  TypographyStyle,
  showError,
} from "#components";
import {
  $confirm,
  confirmStatus,
  cropCanvas,
  deleteResidentImage,
  fromFallRiskToColor,
  fromFimDomainToColor,
  fromImageToCanvas,
  generateResidentImagePutUrl,
  invalidateResidentImageQuery,
  modifyResident,
  resizeCanvas,
  toImage,
  toImageFile,
  updateLatestStatusQueryCache,
  useBedInfiniteQuery,
  useEasy,
  useFacilityQuery,
  useFallRiskEntryInfiniteQuery,
  useIndependenceLevelEntryInfiniteQuery,
  useLatestFallRiskEntryQuery,
  useLatestIndependenceLevelEntryQuery,
  useLatestStatusQuery,
  useListedFalls,
  useNow,
  useQuantizedDateTime,
  useResidentImageQuery,
  useResidentQuery,
  useStatusInfiniteQuery,
  useUsername,
} from "#services";
import { css, cx } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/facilities/$facilityKey/residents/$userKey/")(
  { component: Component },
);

function Component() {
  const { facilityKey, userKey } = Route.useParams();

  const now = useNow(60_000); // 1分間(60,000ミリ秒間)に1回更新する。
  const quantizedNow = useQuantizedDateTime(now, "hour"); // 1時間単位に丸める。
  const [today, setToday] = useState(function () {
    // 便宜上、日付変更時刻を0時ではなく、`TIME_FRAME.DAYTIME`の開始時刻に設定する。
    return now.subtract({ days: now.hour < TIME_FRAME.DAYTIME[0] ? 1 : 0 }).toPlainDate();
  });
  useEffect(
    function () {
      // `now`の更新に伴って更新されるが、実際に日が変わった時のみインスタンスを更新する。
      setToday(function (previous) {
        const next = now.subtract({ days: now.hour < TIME_FRAME.DAYTIME[0] ? 1 : 0 }).toPlainDate();
        return Temporal.PlainDate.compare(previous, next) !== 0 ? next : previous;
      });
    },
    [now],
  );
  const yesterday = useMemo(
    function () {
      return today.subtract({ days: 1 });
    },
    [today],
  );
  const weekAgo = useMemo(
    function () {
      return now.subtract({ days: 7 });
    },
    [now],
  );

  const facilityQuery = useFacilityQuery(facilityKey);
  const { data: facility } = facilityQuery;
  const { t } = useTranslation();
  const residentQuery = useResidentQuery(userKey);
  const { data: resident } = residentQuery;
  const residentImageQuery = useResidentImageQuery(userKey);
  const username = useUsername();
  const [simple, setSimple] = useState(true);
  const [easy] = useEasy();

  const bedInfiniteQuery = useBedInfiniteQuery({
    index: "userKey-gsi",
    condition: [["userKey", "=", userKey]],
  });
  const bed = bedInfiniteQuery.data?.pages.at(0)?.beds.at(0);

  const latestFallRiskEntryQuery = useLatestFallRiskEntryQuery(userKey);
  const fallRiskEntryInfiniteQuery = useFallRiskEntryInfiniteQuery({
    condition: [
      ["userKey", "=", userKey],
      ["assessedAt", "BETWEEN", weekAgo, "AND", now],
    ],
  });

  const fallRiskEntries = useMemo(
    function () {
      return (
        fallRiskEntryInfiniteQuery.data?.pages.flatMap(function (page) {
          return page.fallRiskEntries;
        }) ?? []
      );
    },
    [fallRiskEntryInfiniteQuery.data],
  );

  const latestIndependenceLevelEntryQuery = useLatestIndependenceLevelEntryQuery(userKey);
  const independenceLevelEntryInfiniteQuery = useIndependenceLevelEntryInfiniteQuery({
    condition: [
      ["userKey", "=", userKey],
      ["assessedAt", "BETWEEN", weekAgo, "AND", now],
    ],
  });

  const independenceLevelEntries = useMemo(
    function () {
      return (
        independenceLevelEntryInfiniteQuery.data?.pages.flatMap(function (page) {
          return page.independenceLevelEntries;
        }) ?? []
      );
    },
    [independenceLevelEntryInfiniteQuery.data],
  );

  const { pages: fallPages } = useListedFalls(
    useMemo(
      function () {
        return {
          deleted: DELETION_FLAG.DEFAULT,
          fellAt: [
            ">=",
            today.subtract({ days: 14 }).toPlainDateTime({ hour: TIME_FRAME.DAYTIME[0] }),
          ],
          userKey,
        };
      },
      [today, userKey],
    ),
  );

  const { lastWeekFalls, thisWeekFalls } = useMemo(
    function () {
      const falls = fallPages.flatMap(function (page) {
        return page.falls;
      });
      const thisWeekStart = quantizedNow.subtract({ days: 7 });
      return {
        lastWeekFalls: falls.filter(function (fall) {
          return Temporal.PlainDateTime.compare(fall.fellAt, thisWeekStart) < 0;
        }),
        thisWeekFalls: falls.filter(function (fall) {
          return Temporal.PlainDateTime.compare(fall.fellAt, thisWeekStart) >= 0;
        }),
      };
    },
    [fallPages, quantizedNow],
  );

  const statusInfiniteQuery1 = useStatusInfiniteQuery(
    useMemo(
      function () {
        return {
          index: "userKey-transitionedAt",
          condition: [
            ["userKey", "=", userKey],
            ["transitionedAt", ">", yesterday.toPlainDateTime({ hour: TIME_FRAME.NIGHTTIME[0] })],
          ],
        };
      },
      [userKey, yesterday],
    ),
  );
  const statusInfiniteQuery2 = useStatusInfiniteQuery(
    useMemo(
      function () {
        return {
          index: "userKey-transitionedAt",
          condition: [
            ["userKey", "=", userKey],
            ["transitionedAt", "<=", yesterday.toPlainDateTime({ hour: TIME_FRAME.NIGHTTIME[0] })],
          ],
          descendingOrder: true,
          limit: 1,
        };
      },
      [userKey, yesterday],
    ),
  );

  const { data: status } = useLatestStatusQuery(userKey);

  useEffect(
    function () {
      if (status?.alerted && !status.confirmed) {
        const temporalStatus = {
          ...status,
          confirmed: true,
          confirmedAt: Temporal.Now.plainDateTimeISO("Asia/Tokyo"),
          confirmedBy: username ?? undefined,
          updatedAt: Temporal.Now.plainDateTimeISO("Asia/Tokyo"),
        };
        updateLatestStatusQueryCache(userKey, temporalStatus);
        confirmStatus({ statusKey: status.statusKey });
      }
    },
    [status, userKey, username],
  );

  const statuses = useMemo(
    function () {
      return sort(
        statusInfiniteQuery2.data?.pages
          .flatMap(function (page) {
            return page.statuses;
          })
          .concat(
            statusInfiniteQuery1.data?.pages.flatMap(function (page) {
              return page.statuses;
            }) ?? [],
          ) ?? [],
        function ({ transitionedAt: a }, { transitionedAt: b }) {
          return Temporal.PlainDateTime.compare(a, b);
        },
      );
    },
    [statusInfiniteQuery1.data, statusInfiniteQuery2.data],
  );

  const stateTransitions = useMemo(
    function () {
      return sort(
        statuses.map(function (status) {
          return [status.transitionedAt, status.state] as const;
        }),
        function ([a], [b]) {
          return Temporal.PlainDateTime.compare(a, b);
        },
      );
    },
    [statuses],
  );

  const sleepingHours = useMemo(
    function () {
      const now = Temporal.Now.plainDateTimeISO();
      const start = yesterday.toPlainDateTime({ hour: TIME_FRAME.NIGHTTIME[0] });
      const end = today.toPlainDateTime({ hour: TIME_FRAME.NIGHTTIME[1] });
      const sleepingHours = calculateSleepingHours(
        calculateResidentStateDurations(stateTransitions, {
          period: [start, Temporal.PlainDateTime.compare(end, now) < 0 ? end : now],
        }),
      ).total("minutes");

      return Number.isNaN(sleepingHours) ? 0 : sleepingHours;
    },
    [stateTransitions, today, yesterday],
  );

  const fimItems = useMemo(
    function () {
      return simple
        ? ([
            [FIM_DOMAIN.TRANSFER, [FIM_ITEM.BED_CHAIR_WHEELCHAIR]],
            [FIM_DOMAIN.LOCOMOTION, [FIM_ITEM.WALK_WHEELCHAIR]],
            [FIM_DOMAIN.SOCIAL_COGNITION, [FIM_ITEM.PROBLEM_SOLVING, FIM_ITEM.MEMORY]],
          ] as const)
        : ([
            [
              FIM_DOMAIN.SELF_CARE,
              [
                FIM_ITEM.EATING,
                FIM_ITEM.GROOMING,
                FIM_ITEM.BATHING,
                FIM_ITEM.UPPER_BODY_DRESSING,
                FIM_ITEM.LOWER_BODY_DRESSING,
                FIM_ITEM.TOILETING,
              ],
            ],
            [
              FIM_DOMAIN.SPHINCTER_CONTROL,
              [FIM_ITEM.BLADDER_MANAGEMENT, FIM_ITEM.BOWEL_MANAGEMENT],
            ],
            [
              FIM_DOMAIN.TRANSFER,
              [FIM_ITEM.BED_CHAIR_WHEELCHAIR, FIM_ITEM.TOILET, FIM_ITEM.TUB_SHOWER],
            ],
            [FIM_DOMAIN.LOCOMOTION, [FIM_ITEM.WALK_WHEELCHAIR, FIM_ITEM.STAIRS]],
            [FIM_DOMAIN.COMMUNICATION, [FIM_ITEM.COMPREHENSION, FIM_ITEM.EXPRESSION]],
            [
              FIM_DOMAIN.SOCIAL_COGNITION,
              [FIM_ITEM.SOCIAL_INTERACTION, FIM_ITEM.PROBLEM_SOLVING, FIM_ITEM.MEMORY],
            ],
          ] as const);
    },
    [simple],
  );

  let color = fromFallRiskToColor(latestFallRiskEntryQuery.data?.fallRisk);
  if (status?.state === RESIDENT_STATE.WANDERING) {
    color = "#18181B";
  }

  return (
    <main>
      <PageName>
        {facility && resident ? resident.name + " - " + facility.name : t("%Loading.0%")}
      </PageName>

      {(status?.state === RESIDENT_STATE.PROVIDER_ERROR ||
        status?.state === RESIDENT_STATE.SENSOR_ERROR) && (
        <p
          className={TypographyStyle({
            color: "error",
            fontSize: "14px",
            fontWeight: 500,
            marginTop: "12px",
            whiteSpace: "pre-wrap",
          })}
        >
          {t("%UnableToConnect.0%")}
        </p>
      )}

      <Link
        className={css({
          alignItems: "center",
          display: "flex",
          gap: "4px",
          hideFrom: "sm",
        })}
        params={{ facilityKey }}
        to="/facilities/$facilityKey"
      >
        <LuChevronLeft className={css({ height: "32px", width: "auto" })} />
        <p
          className={TypographyStyle({
            color: "primary",
            fontSize: "16px",
            fontWeight: 500,
            lineHeight: 1,
          })}
        >
          {t("%ResidentList.0%")}
        </p>
      </Link>

      <Breadcrumbs
        className={css(TypographyStyle.raw({ fontSize: "14px", fontWeight: 500, lineHeight: 1 }), {
          display: "flex",
          hideBelow: "sm",
        })}
      >
        <Link
          className={TypographyStyle({
            color: "muted",
            fontSize: "14px",
            lineHeight: 1,
            link: true,
          })}
          params={{ facilityKey }}
          to="/facilities/$facilityKey"
        >
          {t("%ResidentList.0%")}
        </Link>
        <p className={TypographyStyle({ color: "default", fontSize: "14px", lineHeight: 1 })}>
          {t("%HonorificTitle.0%", {
            $: resident ?? { name: t("%Resident.0%") },
          })}
        </p>
      </Breadcrumbs>

      {latestIndependenceLevelEntryQuery.data === null && (
        <div className={css({ marginTop: "12px" })}>
          <p
            className={TypographyStyle({
              color: "error",
              fontSize: "14px",
              fontWeight: 500,
              whiteSpace: "pre-wrap",
            })}
          >
            {t("%InsufficientInformation.0%")}
          </p>
          <Link
            className={css(
              ButtonStyle.raw({
                appearance: "contained",
                borderRadius: "4px",
                color: "primary",
              }),
              {
                marginTop: "12px",
                padding: { smDown: "16px", sm: "10px 16px" },
                width: { smDown: "stretch" },
              },
            )}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/residents"
          >
            {t("%ResidentManagement.1%")}
          </Link>
        </div>
      )}

      <div
        className={css(TypographyStyle.raw({ fontSize: "14px", fontWeight: 500, lineHeight: 1 }), {
          alignItems: "center",
          borderRadius: "8px",
          color: "white",
          display: "flex",
          gap: "8px",
          gridColumn: "1 / -1",
          padding: "6px 8px",
          width: "stretch",
        })}
        style={{
          backgroundColor: color,
          display:
            !status?.alerted || (status.confirmed && status.confirmedBy !== username)
              ? "none"
              : undefined,
        }}
      >
        <LuMegaphone className={css({ height: "24px", width: "auto" })} />
        <p className={css({ flexGrow: 1 })}>
          {t("%ResidentState." + (status?.state ?? RESIDENT_STATE.UNKNOWN) + ".2%")}
        </p>
        <p>{t("%Time.2%", { $: status?.transitionedAt ?? now })}</p>
      </div>

      <div
        className={css({
          columnGap: "16px",
          display: "grid",
          gridTemplateAreas: { smDown: '"a" "b" "c"', sm: '"a b" "a c"' },
          gridTemplateColumns: { smDown: "auto", sm: "auto 1fr" },
          marginTop: "12px",
          rowGap: "12px",
        })}
      >
        <div
          className={css({
            alignItems: "center",
            display: "flex",
            gridArea: "a",
            justifyContent: "center",
          })}
        >
          <div className={css({ position: "relative" })}>
            {residentImageQuery.data ? (
              <img
                className={css({
                  aspectRatio: "1 / 1",
                  borderRadius: "16384px",
                  display: "block",
                  height: "160px",
                })}
                onError={function (event) {
                  console.error(event);
                }}
                onLoad={function (event) {
                  console.debug(event);
                }}
                src={residentImageQuery.data}
              />
            ) : (
              <AvatarPlaceholder
                className={css({
                  aspectRatio: "1 / 1",
                  borderRadius: "16384px",
                  display: "block",
                  height: "160px",
                })}
              />
            )}

            <ImageDeleteButton data-display-none={!residentImageQuery.data} userKey={userKey} />
            <ImageUploadButton userKey={userKey} />
          </div>
        </div>

        <div
          className={css(PaperStyle.raw({ borderRadius: "12px" }), {
            display: "flex",
            gap: "12px",
            gridArea: "b",
            paddingX: "16px",
          })}
        >
          <div className={css({ minWidth: 0, paddingY: "12px" })}>
            <p
              className={TypographyStyle({
                color: "primary/60",
                ellipsis: true,
                fontSize: "14px",
                lineHeight: 1,
              })}
            >
              {t("%Name.0%")}
            </p>
            <p
              className={TypographyStyle({
                ellipsis: true,
                fontSize: "16px",
                fontWeight: 500,
                marginTop: "8px",
              })}
            >
              {resident?.name}
            </p>
          </div>

          <div
            className={css({ borderColor: "border", borderLeftWidth: "1px", borderStyle: "solid" })}
          />

          <div className={css({ minWidth: 0, paddingY: "12px" })}>
            <p
              className={TypographyStyle({
                color: "primary/60",
                ellipsis: true,
                fontSize: "14px",
                lineHeight: 1,
              })}
            >
              {t("%Room.0%")}&nbsp;-&nbsp;{t("%Bed.0%")}
            </p>
            <p
              className={TypographyStyle({
                ellipsis: true,
                fontSize: "16px",
                fontWeight: 500,
                marginTop: "8px",
              })}
            >
              {bed?.roomName}&nbsp;-&nbsp;{bed?.bedName}
            </p>
          </div>

          <div
            className={css({ borderColor: "border", borderLeftWidth: "1px", borderStyle: "solid" })}
          />

          <div className={css({ minWidth: 0, paddingY: "12px" })}>
            <p
              className={TypographyStyle({
                color: "primary/60",
                ellipsis: true,
                fontSize: "14px",
                lineHeight: 1,
              })}
            >
              {t("%Status.0%")}
            </p>
            <p
              className={TypographyStyle({
                ellipsis: true,
                fontSize: "16px",
                fontWeight: 500,
                marginTop: "8px",
              })}
            >
              {t(
                "%ResidentState." +
                  (status?.state
                    ? easy
                      ? status.state === RESIDENT_STATE.WANDERING
                        ? RESIDENT_STATE.GOT_UP
                        : status.state
                      : status.state
                    : RESIDENT_STATE.UNKNOWN) +
                  ".0%",
              )}
            </p>
          </div>
        </div>

        <AlertSettingsPane userKey={userKey} />
      </div>

      <div
        className={css({
          display: "grid",
          gap: "12px",
          gridTemplateColumns: { smDown: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
          marginTop: "12px",
        })}
      >
        <Link
          className={PaperStyle({ borderRadius: "12px", display: "block", padding: "16px 12px" })}
          params={{ facilityKey, userKey }}
          search={{ date: today }}
          to="/facilities/$facilityKey/residents/$userKey/states"
        >
          <div
            className={css({
              alignItems: "center",
              display: "flex",
              gap: "8px",
              justifyContent: "space-between",
              "& > *": { minWidth: 0 },
            })}
          >
            <h2
              className={css({
                alignItems: "center",
                color: "#0284C7",
                display: "flex",
                gap: "4px",
              })}
            >
              <LuMoonStar className={css({ height: "20px", width: "auto" })} />
              <span
                className={TypographyStyle({
                  ellipsis: true,
                  fontSize: "16px",
                  fontWeight: 500,
                  lineHeight: 1,
                })}
              >
                {t("%Sleep.0%")}
              </span>
            </h2>

            <h3 className={css({ alignItems: "center", display: "flex", gap: "4px" })}>
              <span
                className={TypographyStyle({
                  color: "primary/60",
                  ellipsis: true,
                  fontSize: "14px",
                  lineHeight: 1,
                })}
              >
                {t("%Date.2%", { $: today })}
              </span>
              <LuChevronRight
                className={css({ color: "primary/60", height: "16px", width: "auto" })}
              />
            </h3>
          </div>

          <div
            className={css({
              alignItems: "center",
              display: "flex",
              gap: "12px",
              justifyContent: "space-between",
              marginTop: "20px",
              "& > *": { minWidth: 0 },
            })}
          >
            <div>
              <p
                className={TypographyStyle({
                  color: "primary/60",
                  ellipsis: true,
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: 1,
                })}
              >
                {t("%SleepingHours.0%")}
              </p>
              {statuses.length > 0 ? (
                <div
                  className={css(
                    TypographyStyle.raw({ ellipsis: true, fontWeight: 700, lineHeight: 1 }),
                    {
                      alignItems: "baseline",
                      display: "flex",
                      gap: "2px",
                      marginTop: "6px",
                    },
                  )}
                >
                  <span className={TypographyStyle({ color: "primary", fontSize: "24px" })}>
                    {Math.floor(sleepingHours / 60)}
                  </span>
                  <span className={TypographyStyle({ color: "primary/60", fontSize: "14px" })}>
                    {t("%Hour.0%")}
                  </span>
                  <span className={TypographyStyle({ color: "primary", fontSize: "24px" })}>
                    {Math.floor(sleepingHours % 60)}
                  </span>
                  <span className={TypographyStyle({ color: "primary/60", fontSize: "14px" })}>
                    {t("%Minute.0%")}
                  </span>
                </div>
              ) : (
                <p
                  className={TypographyStyle({
                    color: "primary",
                    fontSize: "24px",
                    fontWeight: 700,
                  })}
                >
                  {t("%NoData.1%")}
                </p>
              )}
            </div>

            <StatusBarThumbnail
              className={css({ borderRadius: "6px", overflow: "clip" })}
              date={today}
              easy={easy}
              height={40}
              statuses={statuses}
              width={120}
            />
          </div>
        </Link>

        <Link
          className={PaperStyle({ borderRadius: "12px", display: "block", padding: "16px 12px" })}
          params={{ facilityKey, userKey }}
          search={{ date: today }}
          to="/facilities/$facilityKey/residents/$userKey/fall-risks"
        >
          <div
            className={css({
              alignItems: "center",
              display: "flex",
              gap: "8px",
              justifyContent: "space-between",
              "& > *": { minWidth: 0 },
            })}
          >
            <h2
              className={css({
                alignItems: "center",
                color: "#DC2626",
                display: "flex",
                gap: "4px",
              })}
            >
              <LuOctagonAlert className={css({ height: "20px", width: "auto" })} />
              <span
                className={TypographyStyle({
                  ellipsis: true,
                  fontSize: "16px",
                  fontWeight: 500,
                  lineHeight: 1,
                })}
              >
                {t("%FallRisk.0%")}
              </span>
            </h2>

            <h3 className={css({ alignItems: "center", display: "flex", gap: "4px" })}>
              <span
                className={TypographyStyle({
                  color: "primary/60",
                  ellipsis: true,
                  fontSize: "14px",
                  lineHeight: 1,
                })}
              >
                {t("%Date.2%", { $: latestFallRiskEntryQuery.data?.assessedAt ?? today })}
              </span>
              <LuChevronRight
                className={css({ color: "primary/60", height: "16px", width: "auto" })}
              />
            </h3>
          </div>

          <div
            className={css({
              alignItems: "center",
              display: "flex",
              gap: "12px",
              justifyContent: "space-between",
              marginTop: "20px",
              "& > *": { minWidth: 0 },
            })}
          >
            <div>
              <p
                className={TypographyStyle({
                  color: "primary/60",
                  ellipsis: true,
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: 1,
                })}
              >
                {t("%AssessmentType." + INDEPENDENCE_LEVEL_TYPE.ESTIMATED + ".1%")}
              </p>
              {latestFallRiskEntryQuery.data ? (
                <FallRiskDisplay
                  className={css({ marginTop: "8px" })}
                  fallRisk={latestFallRiskEntryQuery.data?.fallRisk}
                />
              ) : (
                <p
                  className={TypographyStyle({
                    color: "primary",
                    fontSize: "24px",
                    fontWeight: 700,
                  })}
                >
                  {t("%NoData.1%")}
                </p>
              )}
            </div>

            <TimelineThumbnail
              color="#DC2626"
              end={now}
              height={40}
              max={FALL_RISK.SEVERE}
              min={FALL_RISK.MILD}
              start={weekAgo}
              values={fallRiskEntries.map(function ({ assessedAt, fallRisk }) {
                return [assessedAt, fallRisk] as const;
              })}
              width={120}
            />
          </div>
        </Link>

        <Link
          className={PaperStyle({ borderRadius: "12px", display: "block", padding: "16px 12px" })}
          params={{ facilityKey, userKey }}
          search={{ date: today, numberOfDays: 7 }}
          to="/facilities/$facilityKey/residents/$userKey/falls"
        >
          <div
            className={css({
              alignItems: "center",
              display: "flex",
              gap: "8px",
              justifyContent: "space-between",
              "& > *": { minWidth: 0 },
            })}
          >
            <h2
              className={css({
                alignItems: "center",
                color: "#DC2626",
                display: "flex",
                gap: "4px",
              })}
            >
              <LuOctagonAlert className={css({ height: "20px", width: "auto" })} />
              <span
                className={TypographyStyle({
                  ellipsis: true,
                  fontSize: "16px",
                  fontWeight: 500,
                  lineHeight: 1,
                })}
              >
                {t("%Fall.0%")}
              </span>
            </h2>

            <h3 className={css({ alignItems: "center", display: "flex", gap: "4px" })}>
              <LuChevronRight
                className={css({ color: "primary/60", height: "16px", width: "auto" })}
              />
            </h3>
          </div>

          <div
            className={css({
              alignItems: "center",
              display: "flex",
              gap: "12px",
              justifyContent: "space-between",
              marginTop: "20px",
              "& > *": { minWidth: 0 },
            })}
          >
            <div>
              <p
                className={TypographyStyle({
                  color: "primary/60",
                  ellipsis: true,
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: 1,
                })}
              >
                {t("%NumberOfFalls.0%")}
              </p>
              <div
                className={css(
                  TypographyStyle.raw({ ellipsis: true, fontWeight: 700, lineHeight: 1 }),
                  {
                    alignItems: "baseline",
                    display: "flex",
                    gap: "2px",
                    marginTop: "6px",
                  },
                )}
              >
                <span className={TypographyStyle({ color: "primary", fontSize: "24px" })}>
                  {thisWeekFalls.length}
                </span>
                <span className={TypographyStyle({ color: "primary/60", fontSize: "14px" })}>
                  {t("%NumberOfFalls.1%")}
                </span>
              </div>
              <div
                className={css({
                  alignItems: "center",
                  display: "flex",
                  gap: "2px",
                  marginTop: "4px",
                })}
              >
                <p
                  className={TypographyStyle({
                    color: "primary/60",
                    fontSize: "12px",
                    fontWeight: 500,
                    lineHeight: 1,
                  })}
                >
                  {t("%WeekOverWeek.0%")}
                </p>
                {thisWeekFalls.length > lastWeekFalls.length && (
                  <LuArrowUpRight
                    className={css({ color: "primary/60", height: "16px", width: "auto" })}
                  />
                )}
                {thisWeekFalls.length === lastWeekFalls.length && (
                  <LuArrowRight
                    className={css({ color: "primary/60", height: "16px", width: "auto" })}
                  />
                )}
                {thisWeekFalls.length < lastWeekFalls.length && (
                  <LuArrowDownRight
                    className={css({ color: "primary/60", height: "16px", width: "auto" })}
                  />
                )}
              </div>
            </div>
          </div>
        </Link>
      </div>

      <h2
        className={css(TypographyStyle.raw({ ellipsis: true, lineHeight: 1 }), {
          alignItems: "center",
          display: "flex",
          gap: "16px",
          marginTop: "24px",
        })}
      >
        <span className={TypographyStyle({ fontSize: "20px", fontWeight: 700 })}>
          {t("%IndependenceLevel.0%")}
        </span>
        <div className={css({ alignItems: "center", display: "flex", gap: "8px" })}>
          <span className={TypographyStyle({ color: "primary/60", fontSize: "14px" })}>
            {t("%IndependenceLevel.1%")}
          </span>
          <input
            checked={simple}
            className={SwitchStyle({ display: "block" })}
            onChange={function (event) {
              setSimple(event.target.checked);
            }}
            type="checkbox"
          />
        </div>
      </h2>

      {fimItems.map(function ([domain, items]) {
        return (
          <Fragment key={domain}>
            <h2
              className={TypographyStyle({
                ellipsis: true,
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: 1,
                marginTop: "16px",
              })}
              style={{ color: fromFimDomainToColor(domain) }}
            >
              {t("%FimDomain." + domain + ".0%")}
            </h2>
            <div
              className={css({
                display: "grid",
                gap: "12px",
                gridTemplateColumns: { smDown: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                marginTop: "12px",
              })}
            >
              {items.map(function (item) {
                return (
                  <Link
                    className={PaperStyle({
                      borderRadius: "12px",
                      display: "block",
                      minWidth: 0,
                      padding: "16px 12px",
                    })}
                    key={item}
                    params={{ facilityKey, fimItem: item, userKey }}
                    search={{ date: today }}
                    to="/facilities/$facilityKey/residents/$userKey/independence-levels/$fimItem"
                  >
                    <div
                      className={css({
                        alignItems: "center",
                        display: "flex",
                        gap: "8px",
                        justifyContent: "space-between",
                        "& > *": { minWidth: 0 },
                      })}
                    >
                      <h2
                        className={css({
                          alignItems: "center",
                          // color: "#0284C7",
                          display: "flex",
                          gap: "4px",
                        })}
                        style={{ color: fromFimDomainToColor(domain) }}
                      >
                        <FimDomainIcon
                          className={css({ height: "20px", width: "auto" })}
                          value={domain}
                        />
                        <span
                          className={TypographyStyle({
                            ellipsis: true,
                            fontSize: "16px",
                            fontWeight: 500,
                            lineHeight: 1,
                          })}
                        >
                          {t("%FimItem." + item + ".0%")}
                        </span>
                      </h2>

                      {latestIndependenceLevelEntryQuery.data && (
                        <h3 className={css({ alignItems: "center", display: "flex", gap: "4px" })}>
                          <span
                            className={TypographyStyle({
                              color: "primary/60",
                              ellipsis: true,
                              fontSize: "14px",
                              lineHeight: 1,
                            })}
                          >
                            {t("%DateTime.2%", {
                              $: latestIndependenceLevelEntryQuery.data.assessedAt,
                            })}
                          </span>
                          <LuChevronRight
                            className={css({ color: "primary/60", height: "16px", width: "auto" })}
                          />
                        </h3>
                      )}
                    </div>

                    {independenceLevelEntries.length > 0 ? (
                      <div
                        className={css({
                          alignItems: "center",
                          display: "flex",
                          gap: "12px",
                          justifyContent: "space-between",
                          marginTop: "20px",
                          "& > *": { minWidth: 0 },
                        })}
                      >
                        <div>
                          <p
                            className={TypographyStyle({
                              fontSize: "24px",
                              fontWeight: 700,
                              lineHeight: 1,
                            })}
                          >
                            {t(
                              "%IndependenceLevel." +
                                independenceLevelEntries[independenceLevelEntries.length - 1]
                                  .independenceLevels[item] +
                                ".0%",
                            )}
                          </p>
                          {independenceLevelEntries.length > 1 && (
                            <div
                              className={css({
                                alignItems: "center",
                                color: "primary/60",
                                display: "flex",
                                gap: "4px",
                                marginTop: "6px",
                              })}
                            >
                              <p
                                className={TypographyStyle({
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  lineHeight: 1,
                                })}
                              >
                                {t("%DayOverDay.0%")}
                              </p>
                              {independenceLevelEntries[independenceLevelEntries.length - 1]
                                .independenceLevels[item] >
                              independenceLevelEntries[independenceLevelEntries.length - 2]
                                .independenceLevels[item] ? (
                                <LuArrowUpRight
                                  className={css({ height: "16px", width: "auto" })}
                                />
                              ) : independenceLevelEntries[independenceLevelEntries.length - 1]
                                  .independenceLevels[item] <
                                independenceLevelEntries[independenceLevelEntries.length - 2]
                                  .independenceLevels[item] ? (
                                <LuArrowDownRight
                                  className={css({ height: "16px", width: "auto" })}
                                />
                              ) : (
                                <LuArrowRight className={css({ height: "16px", width: "auto" })} />
                              )}
                            </div>
                          )}
                        </div>

                        <TimelineThumbnail
                          color={fromFimDomainToColor(domain)}
                          end={now}
                          height={40}
                          max={INDEPENDENCE_LEVEL.COMPLETE_INDEPENDENCE}
                          min={INDEPENDENCE_LEVEL.TOTAL_ASSISTANCE}
                          start={weekAgo}
                          values={independenceLevelEntries.map(function ({
                            assessedAt,
                            independenceLevels,
                          }) {
                            return [assessedAt, independenceLevels[item]] as const;
                          })}
                          width={120}
                        />
                      </div>
                    ) : (
                      <p
                        className={TypographyStyle({
                          color: "primary",
                          fontSize: "24px",
                          fontWeight: 700,
                          marginTop: "20px",
                        })}
                      >
                        {t("%NoData.1%")}
                      </p>
                    )}
                  </Link>
                );
              })}
            </div>
          </Fragment>
        );
      })}
      <div className={css({ height: "16px" })} />
    </main>
  );
}

function AlertSettingsPane({ userKey }: { userKey: UserKey }) {
  const { t } = useTranslation();

  const dialog = useDialog();
  const id = useId();
  const residentQuery = useResidentQuery(userKey);
  const { data: resident } = residentQuery;
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const formSchema = useMemo(
    function () {
      return v.object({
        [RESIDENT_STATE.GOT_UP]: v.object({
          enabled: v.pipe(
            v.nullable(v.boolean()),
            v.transform(function (value) {
              return value || null;
            }),
          ),
        }),
        [RESIDENT_STATE.WANDERING]: v.object({
          enabled: v.boolean(t("%Error." + ERROR_CODE.INVALID_DATA + ".0%")),
          range: v.tuple([
            v.message(TimeSchema(), t("%Error." + ERROR_CODE.INVALID_TIME + ".0%")),
            v.message(TimeSchema(), t("%Error." + ERROR_CODE.INVALID_TIME + ".0%")),
          ]),
          threshold: v.pipe(
            v.number(t("%Error." + ERROR_CODE.INVALID_DATA + ".0%")),
            v.integer(t("%Error." + ERROR_CODE.INVALID_DATA + ".0%")),
            v.minValue(1, t("%Error." + ERROR_CODE.INVALID_DATA + ".0%")),
            v.maxValue(1440, t("%Error." + ERROR_CODE.INVALID_DATA + ".0%")),
            v.transform(function (duration) {
              return Temporal.Duration.from({ minutes: duration });
            }),
          ),
        }),
      });
    },
    [t],
  );

  const form = useForm({
    defaultValues: useMemo(
      function (): v.InferInput<typeof formSchema> {
        return {
          [RESIDENT_STATE.GOT_UP]: {
            enabled: resident?.alertSettings[RESIDENT_STATE.GOT_UP].enabled ?? null,
          },
          [RESIDENT_STATE.WANDERING]: {
            enabled: resident?.alertSettings[RESIDENT_STATE.WANDERING].enabled ?? false,
            range: [
              resident?.alertSettings[RESIDENT_STATE.WANDERING].range[0].toString({
                roundingMode: "trunc",
                smallestUnit: "minute",
              }) ?? "00:00",
              resident?.alertSettings[RESIDENT_STATE.WANDERING].range[1].toString({
                roundingMode: "trunc",
                smallestUnit: "minute",
              }) ?? "00:00",
            ],
            threshold:
              resident?.alertSettings[RESIDENT_STATE.WANDERING].threshold.total("minutes") ?? 5,
          },
        };
      },
      [resident],
    ),
    async onSubmit({ value: input }) {
      if (!resident) {
        return;
      }

      try {
        console.info("フォームをパースします。", input);
        const output = parse(formSchema, input);
        console.info("フォームをパースしました。", output);
        await modifyResident({ alertSettings: output, userKey });
        toast.success(
          tt({ ja: "{{name}}さんのアラート設定を保存しました。", _: { name: resident.name } }),
        );
      } catch (error) {
        console.error("フォームの送信中にエラーが発生しました。", error);
        toast.error(
          error instanceof Error
            ? error.message
            : tt({
                ja: "{{name}}さんのアラート設定を保存出来ませんでした。",
                _: { name: resident.name },
              }),
        );
      }
    },
    async onSubmitInvalid({ value: input }) {
      console.warn("フォームの入力が無効です。", input);
      toast.error(t("%Error." + ERROR_CODE.INVALID_FORM + ".0%"));
    },
    validators: {
      onSubmit: formSchema,
    },
  });

  const formStoreStateIsSubmitting = useSelector(form.store, function (state) {
    return state.isSubmitting;
  });

  const styles = DialogStyle.raw();

  const DEBOUNCE_TIME = 1000;

  return (
    <form
      className={css(PaperStyle.raw({ borderRadius: "12px" }), {
        display: "flex",
        flexDirection: { smDown: "column", sm: "row" },
        gap: "12px",
        padding: { smDown: "16px", sm: "0 16px" },
      })}
      id={id}
      onSubmit={function (event) {
        // 今から処理を開始するため、
        // デバウンス処理のためのタイムアウトをクリアする。
        clearTimeout(timeout.current);
        timeout.current = undefined;

        event.preventDefault();
        form.handleSubmit().then(function () {
          dialog.setOpen(false);
        });
      }}
    >
      <div
        className={css({
          alignItems: { smDown: "center" },
          display: "flex",
          gap: "8px",
          gridArea: "c",
          flexDirection: { smDown: "row", sm: "column" },
          justifyContent: { smDown: "space-between" },
          paddingY: { sm: "12px" },
        })}
      >
        <p
          className={css(TypographyStyle.raw({ ellipsis: true, fontSize: "14px", lineHeight: 1 }), {
            color: { smDown: "primary", sm: "primary/60" },
            fontSize: { smDown: "16px", sm: "14px" },
          })}
        >
          {t("%ResidentState." + RESIDENT_STATE.GOT_UP + ".1%")}
        </p>
        <form.Field name={`${RESIDENT_STATE.GOT_UP}.enabled`}>
          {function (field) {
            return (
              <input
                checked={field.state.value ?? false}
                className={SwitchStyle({ display: "block" })}
                disabled={formStoreStateIsSubmitting || residentQuery.isFetching}
                onChange={async function (event) {
                  field.setValue(event.target.checked || null);

                  clearTimeout(timeout.current);
                  timeout.current = setTimeout(async function () {
                    form.handleSubmit();
                  }, DEBOUNCE_TIME);
                }}
                type="checkbox"
              />
            );
          }}
        </form.Field>
      </div>

      <div
        className={css({
          borderBottomWidth: { smDown: "1px" },
          borderColor: "border",
          borderLeftWidth: { sm: "1px" },
          borderStyle: "solid",
          width: { smDown: "100%" },
        })}
      />

      <div
        className={css({
          alignItems: { sm: "center" },
          display: "flex",
          gap: { smDown: "16px", sm: "8px" },
          flexDirection: { smDown: "column", sm: "row" },
          justifyContent: { smDown: "space-between" },
        })}
      >
        <div
          className={css({
            alignItems: { smDown: "center" },
            display: "flex",
            gap: "8px",
            flexDirection: { smDown: "row", sm: "column" },
            justifyContent: { smDown: "space-between" },
            paddingY: { sm: "12px" },
          })}
        >
          <p
            className={css(TypographyStyle.raw({ ellipsis: true, lineHeight: 1 }), {
              color: { smDown: "primary", sm: "primary/60" },
              fontSize: { smDown: "16px", sm: "14px" },
            })}
          >
            {t("%ResidentState." + RESIDENT_STATE.WANDERING + ".1%")}
          </p>
          <form.Field name={`${RESIDENT_STATE.WANDERING}.enabled`}>
            {function (field) {
              return (
                <input
                  checked={field.state.value ?? false}
                  className={SwitchStyle({ display: "block" })}
                  disabled={formStoreStateIsSubmitting || residentQuery.isFetching}
                  onChange={async function (event) {
                    field.setValue(event.target.checked);

                    clearTimeout(timeout.current);
                    timeout.current = setTimeout(async function () {
                      form.handleSubmit();
                    }, DEBOUNCE_TIME);
                  }}
                  type="checkbox"
                />
              );
            }}
          </form.Field>
        </div>

        <Dialog.RootProvider value={dialog}>
          <Dialog.Trigger asChild>
            <div
              className={css(TypographyStyle.raw({ lineHeight: 1 }), {
                alignItems: "center",
                backgroundColor: { sm: "#F4F4F5" },
                borderRadius: "6px",
                columnGap: "6px",
                cursor: "pointer",
                display: "grid",
                fontSize: { smDown: "16px", sm: "14px" },
                gridTemplateColumns: { smDown: "repeat(2, auto) 1fr", sm: "repeat(2, auto)" },
                padding: { smDown: 0, sm: "8px 6px" },
                rowGap: { smDown: "8px", sm: "4px" },
                width: { smDown: "stretch", sm: "auto" },
              })}
            >
              <LuClock
                className={css({
                  color: "primary/60",
                  display: { smDown: "block", sm: "none" },
                  height: { smDown: "16px", sm: "14px" },
                  width: "auto",
                })}
              />
              <p className={TypographyStyle({ color: "primary/60", ellipsis: true })}>
                {t("%TargetTime.0%")}
              </p>
              <p
                className={css(TypographyStyle.raw({ ellipsis: true, textAlign: "right" }), {
                  color: { smDown: "primary/60", sm: "primary" },
                })}
              >
                <form.Subscribe
                  selector={function (state) {
                    return state.values[RESIDENT_STATE.WANDERING].range;
                  }}
                >
                  {function (range) {
                    let start, stop;
                    try {
                      start = parse(TimeSchema(), range[0]);
                      stop = parse(TimeSchema(), range[1]);
                    } catch {
                      return null;
                    }
                    return Temporal.PlainTime.compare(start, stop) === 0
                      ? t("%AtAnyTime.0%")
                      : t("%Time.1%", { $: start }) + " - " + t("%Time.1%", { $: stop });
                  }}
                </form.Subscribe>
              </p>
              <LuAlarmClock
                className={css({
                  color: "primary/60",
                  display: { smDown: "block", sm: "none" },
                  height: { smDown: "16px", sm: "14px" },
                  width: "auto",
                })}
              />
              <p className={TypographyStyle({ color: "primary/60", ellipsis: true })}>
                {t("%JudgmentTime.0%")}
              </p>
              <p
                className={css(TypographyStyle.raw({ ellipsis: true, textAlign: "right" }), {
                  color: { smDown: "primary/60", sm: "primary" },
                })}
              >
                <form.Subscribe
                  selector={function (state) {
                    return state.values[RESIDENT_STATE.WANDERING].threshold;
                  }}
                >
                  {function (threshold) {
                    return !Number.isNaN(threshold) ? t("%Minute.1%", { $: threshold }) : null;
                  }}
                </form.Subscribe>
              </p>
            </div>
          </Dialog.Trigger>
          <Portal>
            <Dialog.Backdrop className={css(styles.backdrop)} />
            <Dialog.Positioner className={css(styles.positioner)}>
              <Dialog.Content
                className={css(styles.content, {
                  maxHeight: "332px",
                  maxWidth: "400px",
                  overflow: "auto",
                })}
              >
                <Dialog.Title className={css(styles.title)}>
                  {t("%ResidentState." + RESIDENT_STATE.WANDERING + ".1%")}
                </Dialog.Title>
                <Dialog.Description className={css(styles.description)}>
                  {t("%Alert.0%")}
                </Dialog.Description>

                <div className={css({ display: "flex", gap: "4px", marginTop: "16px" })}>
                  <form.Field name={`${RESIDENT_STATE.WANDERING}.range[0]`}>
                    {function (field) {
                      return (
                        <FormField
                          caution={field.state.meta.errors
                            .map(function (error) {
                              return error?.message;
                            })
                            .filter(function (message) {
                              return Boolean(message);
                            })
                            .join("")}
                          className={css({ flexGrow: 1 })}
                          disabled={formStoreStateIsSubmitting || residentQuery.isFetching}
                          invalid={field.state.meta.errors.length > 0}
                          label={t("%TargetTime.1%")}
                          required
                        >
                          <FormFieldInput
                            className={FormFieldInputStyle({ flexGrow: 1, width: "100%" })}
                            id={field.name}
                            onBlur={function () {
                              field.handleBlur();
                            }}
                            onChange={function (event) {
                              field.handleChange(event.target.value);
                            }}
                            type="time"
                            value={String(field.state.value)}
                          />
                        </FormField>
                      );
                    }}
                  </form.Field>

                  <FormField label="&nbsp;">
                    <div
                      className={css({
                        borderColor: "black",
                        borderBottomWidth: "1px",
                        borderStyle: "solid",
                        height: "40%",
                        width: "4px",
                      })}
                    />
                  </FormField>

                  <form.Field name={`${RESIDENT_STATE.WANDERING}.range[1]`}>
                    {function (field) {
                      return (
                        <FormField
                          caution={field.state.meta.errors
                            .map(function (error) {
                              return error?.message;
                            })
                            .filter(function (message) {
                              return Boolean(message);
                            })
                            .join("")}
                          className={css({ flexGrow: 1 })}
                          disabled={formStoreStateIsSubmitting || residentQuery.isFetching}
                          invalid={field.state.meta.errors.length > 0}
                          label={t("%TargetTime.2%")}
                          required
                        >
                          <FormFieldInput
                            className={FormFieldInputStyle({ flexGrow: 1, width: "100%" })}
                            id={field.name}
                            onBlur={function () {
                              field.handleBlur();
                            }}
                            onChange={function (event) {
                              field.handleChange(event.target.value);
                            }}
                            type="time"
                            value={String(field.state.value)}
                          />
                        </FormField>
                      );
                    }}
                  </form.Field>
                </div>

                <form.Field name={`${RESIDENT_STATE.WANDERING}.threshold`}>
                  {function (field) {
                    return (
                      <FormField
                        caution={field.state.meta.errors
                          .map(function (error) {
                            return error?.message;
                          })
                          .filter(function (message) {
                            return Boolean(message);
                          })
                          .join("")}
                        className={css({ flexGrow: 1, marginTop: "12px" })}
                        disabled={formStoreStateIsSubmitting || residentQuery.isFetching}
                        invalid={field.state.meta.errors.length > 0}
                        label={t("%JudgmentTime.0%")}
                        required
                      >
                        <div className={css({ display: "flex", alignItems: "center", gap: "4px" })}>
                          <FormFieldInput
                            className={css(FormFieldInputStyle.raw({ width: "100%" }), {
                              maxWidth: "160px",
                            })}
                            id={field.name}
                            onBlur={function () {
                              field.handleBlur();
                            }}
                            onChange={function (event) {
                              field.handleChange(Number.parseInt(event.target.value));
                            }}
                            step={1}
                            type="number"
                            value={field.state.value}
                          />
                          <span
                            className={TypographyStyle({ fontSize: "14px", lineHeight: "10 / 7" })}
                          >
                            {t("%Minute.0%")}
                          </span>
                        </div>
                      </FormField>
                    );
                  }}
                </form.Field>

                <div
                  className={css({
                    display: "flex",
                    gap: "8px",
                    justifyContent: "flex-end",
                    marginTop: "16px",
                  })}
                >
                  <button
                    className={ButtonStyle2({ color: "white" })}
                    disabled={formStoreStateIsSubmitting}
                    onClick={function () {
                      dialog.setOpen(false);

                      const {
                        options: { defaultValues },
                      } = form;
                      if (defaultValues) {
                        form.setFieldValue(
                          `${RESIDENT_STATE.WANDERING}.range`,
                          defaultValues[RESIDENT_STATE.WANDERING].range,
                        );
                        form.setFieldValue(
                          `${RESIDENT_STATE.WANDERING}.threshold`,
                          defaultValues[RESIDENT_STATE.WANDERING].threshold,
                        );
                      }
                    }}
                    type="button"
                  >
                    {t("%Cancel.0%")}
                  </button>
                  <button
                    className={ButtonStyle2({ color: "black" })}
                    disabled={formStoreStateIsSubmitting}
                    form={id}
                    type="submit"
                  >
                    {formStoreStateIsSubmitting && (
                      <Spinner color="#FAFAFA" data-left size="14px" type="a" />
                    )}
                    {t("%Save.0%")}
                  </button>
                </div>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.RootProvider>
      </div>
    </form>
  );
}

type ImageDeleteButtonProperties = Merge<
  React.ComponentProps<"div">,
  { onClick?: never; userKey: UserKey }
>;
function ImageDeleteButton({ className, userKey, ...properties }: ImageDeleteButtonProperties) {
  const { t } = useTranslation();
  const [state, deleteImage] = useAsyncFn(async function () {
    if (
      !(await $confirm({
        color: "error",
        content: t("%DeleteResidentImage.1%"),
        title: t("%DeleteResidentImage.0%"),
      }))
    ) {
      return;
    }

    await deleteResidentImage({ userKey });

    invalidateResidentImageQuery(userKey);
  }, []);

  return (
    <div
      {...properties}
      className={cx(
        css(ButtonStyle.raw({ icon: true }), {
          alignItems: "center",
          aspectRatio: "1 / 1",
          backgroundColor: "#18181B80",
          borderRadius: "16384px",
          display: "flex",
          height: "40px",
          justifyContent: "center",
          position: "absolute",
          right: 0,
          top: 0,
        }),
        className,
      )}
      onClick={async function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (state.loading) {
          return;
        }

        await deleteImage();
      }}
    >
      {state.loading ? (
        <LuLoaderCircle
          className={css({
            animation: "rotate 1s linear infinite",
            color: "#FAFAFA",
            height: "24px",
            width: "auto",
          })}
        />
      ) : (
        <LuTrash2 className={css({ color: "#FAFAFA", height: "24px", width: "auto" })} />
      )}
    </div>
  );
}

function ImageUploadButton({ userKey }: { userKey: UserKey }) {
  const [loading, setLoading] = useState(false);
  const [state, uploadImage] = useAsyncFn(
    async function (file: File) {
      const canvas = fromImageToCanvas(await toImage(file));
      const resizedCanvas = resizeCanvas(canvas, {
        height:
          canvas.height > canvas.width ? Math.round(canvas.height * (1024 / canvas.width)) : 1024,
        width:
          canvas.width > canvas.height ? Math.round(canvas.width * (1024 / canvas.height)) : 1024,
      });
      const croppedCanvas = cropCanvas(resizedCanvas, { height: 1024, width: 1024 });
      const url = await generateResidentImagePutUrl({ userKey });

      await fetch(url, {
        body: await toImageFile(croppedCanvas, "image/jpeg", { quality: 75 }),
        method: "PUT",
      });

      invalidateResidentImageQuery(userKey);
    },
    [userKey],
  );

  return (
    <label
      className={css(ButtonStyle.raw({ icon: true }), {
        alignItems: "center",
        aspectRatio: "1 / 1",
        backgroundColor: "#18181B80",
        borderRadius: "16384px",
        bottom: 0,
        display: "flex",
        height: "40px",
        justifyContent: "center",
        position: "absolute",
        right: 0,
      })}
    >
      {state.loading ? (
        <LuLoaderCircle
          className={css({
            animation: "rotate 1s linear infinite",
            color: "#FAFAFA",
            height: "24px",
            width: "auto",
          })}
        />
      ) : (
        <LuCamera className={css({ color: "#FAFAFA", height: "24px", width: "auto" })} />
      )}
      <input
        // ネイティブアプリケーションにも対応するため、`"image/*"`を指定する。
        // `"image/jpeg,image/png"`のように複数指定は出来ない。
        accept="image/*"
        hidden
        onChange={async function (event) {
          if (loading) {
            return;
          }
          setLoading(true);

          const file = event.target.files?.item(0);
          if (!file) {
            setLoading(false);
            return;
          }

          try {
            await uploadImage(file);
          } catch (error) {
            showError(error);
          } finally {
            setLoading(false);
          }
        }}
        type="file"
      />
    </label>
  );
}
